import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { useApi } from '../../hooks/useApi';
import { useCustomAlert } from '../../components/common/CustomAlert';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';

const ClientFeaturesDialog = ({ open, onClose, client }) => {
  const api = useApi();
  const { showConfirm } = useCustomAlert();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [changedFeatures, setChangedFeatures] = useState(new Set());
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  const loadFeatures = useCallback(async () => {
    if (!client?.TenantId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/features/${client.TenantId}`);
      const data = response.data?.data || response.data;
      setFeatures(data.features || []);
    } catch (err) {
      console.error('Error loading features:', err);
      setError(getApiErrorMessage(err, 'Failed to load features'));
    } finally {
      setLoading(false);
    }
  }, [api, client?.TenantId]);

  useEffect(() => {
    if (open && client?.TenantId) {
      loadFeatures();
      setChangedFeatures(new Set());
    }
  }, [open, client?.TenantId, loadFeatures]);

  const handleToggleFeature = (featureId) => {
    setFeatures(prev => prev.map(f => {
      if (f.featureId === featureId) {
        const updated = { ...f, isEnabled: !f.isEnabled };
        setChangedFeatures(prev => {
          const newSet = new Set(prev);
          newSet.add(featureId);
          return newSet;
        });
        return updated;
      }
      return f;
    }));
  };

  const handleSaveChanges = async () => {
    if (changedFeatures.size === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const enabledIds = [];
      const disabledIds = [];

      features.forEach(f => {
        if (changedFeatures.has(f.featureId)) {
          if (f.isEnabled) {
            enabledIds.push(f.featureId);
          } else {
            disabledIds.push(f.featureId);
          }
        }
      });

      if (enabledIds.length > 0) {
        await api.post(`/features/${client.TenantId}/bulk`, {
          featureIds: enabledIds,
          isEnabled: true
        });
      }

      if (disabledIds.length > 0) {
        await api.post(`/features/${client.TenantId}/bulk`, {
          featureIds: disabledIds,
          isEnabled: false
        });
      }

      onClose();
    } catch (err) {
      console.error('Error saving features:', err);
      setError(getApiErrorMessage(err, 'Failed to save changes'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (changedFeatures.size > 0) {
      const confirmed = await showConfirm('You have unsaved changes. Are you sure you want to close?', { title: 'Unsaved Changes', confirmText: 'Close' });
      if (confirmed) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const enabledCount = features.filter(f => f.isEnabled).length;
  const totalCount = features.length;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: 'min(600px, 80vh)' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Manage Features - {client?.TenantName}
          </Typography>
          <Chip 
            label={`${enabledCount} / ${totalCount} Enabled`}
            color={enabledCount > 0 ? 'success' : 'default'}
            size="small"
          />
        </Box>
        {client?.TenantType && (
          <Typography variant="caption" color="text.secondary">
            Client Type: {client.TenantType}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>

        {changedFeatures.size > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have {changedFeatures.size} unsaved change{changedFeatures.size > 1 ? 's' : ''}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : features.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography color="text.secondary">
              No features available
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="3.75rem" align="center">Enabled</TableCell>
                  <TableCell>Feature</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell width="6.25rem" align="center">Status</TableCell>
                  <TableCell width="9.375rem">Last Modified</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature) => {
                  const isChanged = changedFeatures.has(feature.featureId);
                  return (
                    <TableRow 
                      key={feature.featureId}
                      hover
                      sx={{ 
                        bgcolor: isChanged ? 'action.hover' : 'inherit',
                        '& td': { 
                          borderLeft: isChanged ? '3px solid' : 'none',
                          borderLeftColor: 'primary.main'
                        }
                      }}
                    >
                      <TableCell align="center">
                        <Checkbox
                          checked={feature.isEnabled}
                          onChange={() => handleToggleFeature(feature.featureId)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {feature.featureName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {feature.featureCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {feature.featureDescription || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {feature.isEnabled ? (
                          <Tooltip title="Enabled">
                            <CheckCircle color="success" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Disabled">
                            <Cancel color="disabled" fontSize="small" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {feature.enabledDate ? (
                          <Box>
                            <Typography variant="caption" display="block">
                              {new Date(feature.enabledDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              by {feature.enabledBy || 'system'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Never enabled
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveChanges}
          variant="contained"
          disabled={saving || changedFeatures.size === 0}
          startIcon={saving && <CircularProgress size={16} />}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientFeaturesDialog;

